import React, { useEffect, useRef } from 'react';
import { GetSoDto, useGeneralSoContext, useSessionStorage, PROPERTY_INFO_KEY, SoPropertyInfoInput, PropertyTypeEnum, useCalculationInput } from 'src/common';
import { HousePropertyInfoForm, HousePropertyInfoData } from 'src/components/forms/housePropertyInfo.form';
import { AddressForm, AddressData } from 'src/components/forms/address.form';
import { FormRef } from 'src/components/forms/form';
import { NavigationService } from 'src/common/utils/navigation.util';
import { AppRoutes } from './_router/app.routes';
import { useGetSo } from 'src/common/api/apollo/queries/useGetSo.query';
import { ContainerComponent, StepHeader, FormDisclaimer, NavigationButtons, LoaderSquare, Flex } from 'src/components';
import { Helmet } from 'react-helmet-async';

export const PropertyInfoHousePage: React.FC = () => {
  const { so, refetch, loading } = useGetSo();
  const { soInitialData, setSoInitialData } = useGeneralSoContext();
  const { updatePropertyInfo } = useCalculationInput();
  const { setValue: setPropertyInfo, getValue: getPropertyInfo } = useSessionStorage<SoPropertyInfoInput>(PROPERTY_INFO_KEY);
  const houseFormRef = useRef<FormRef>(null);
  const addressFormRef = useRef<FormRef>(null);

  useEffect(() => {
    if (so) {
      refetch();
    }
  }, []);

  const handleBack = () => {
    NavigationService.navigate(AppRoutes.FUNNEL.RISKS);
  };

  const handleFormSubmit = (formData: HousePropertyInfoData) => {
    const existingPropertyInfo = so?.productTypeSpecific?.propertyInfo;

    const propertyInfo = {
      type: PropertyTypeEnum.HOUSE,
      city: formData.city || '',
      street: formData.street || '',
      houseNumber: formData.houseNumber || '',
      area: formData.area || 0,
      floor: formData.floor || ['groundFloor'],
      masonry: formData.masonry ?? true,
      constructionYear: {
        exactYear: formData.before1964 ? 1963 : 1964,
        before1964: formData.before1964 ?? false,
      },
      cityKompareId: formData.cityKompareId || existingPropertyInfo?.cityKompareId || '',
      streetKompareId: formData.streetKompareId || existingPropertyInfo?.streetKompareId || '',
      cityParentName: formData.cityParentName || existingPropertyInfo?.cityParentName || '',
      zipCode: typeof formData.zipCode === 'number' ? formData.zipCode : (existingPropertyInfo?.zipCode ?? 0),
      usage: formData.usage || 'residential',
    };

    setSoInitialData({ ...soInitialData, propertyInfo });
    setPropertyInfo(propertyInfo);
    updatePropertyInfo({ area: propertyInfo.area, constructionYear: propertyInfo.constructionYear });
    NavigationService.navigate(AppRoutes.FUNNEL.CUSTOMER);
  };

  const modelToForm = (so?: GetSoDto) => {
    const propertyInfo = getPropertyInfo() ?? so?.productTypeSpecific?.propertyInfo;

    const base = {
      area: propertyInfo?.area || undefined,
      before1964: propertyInfo?.constructionYear?.before1964 || undefined,
      city: propertyInfo?.city || '',
      cityKompareId: propertyInfo?.cityKompareId || undefined,
      cityParentName: propertyInfo?.cityParentName || undefined,
      street: propertyInfo?.street || '',
      streetKompareId: propertyInfo?.streetKompareId || undefined,
      zipCode: propertyInfo?.zipCode ?? undefined,
      houseNumber: propertyInfo?.houseNumber || '',
      floor: propertyInfo?.floor || undefined,
      masonry: propertyInfo?.masonry ?? undefined,
      usage: propertyInfo?.usage ?? undefined,
    };
    return {
      propertyData: base as HousePropertyInfoData,
      addressData: {
        city: base.city,
        cityKompareId: base.cityKompareId,
        cityParentName: base.cityParentName,
        street: base.street,
        streetKompareId: base.streetKompareId,
        zipCode: base.zipCode,
        houseNumber: base.houseNumber,
      } as AddressData,
    };
  };

  const { propertyData, addressData } = modelToForm(so || undefined);

  const handleSubmitBoth = () => {
    const houseValid = houseFormRef.current?.validateForm?.() ?? false;
    const addressValid = addressFormRef.current?.validateForm?.() ?? false;

    if (!houseValid || !addressValid) return;
    const houseValues = houseFormRef.current?.getValues?.() ?? {} as Partial<HousePropertyInfoData>;
    const addressValues = addressFormRef.current?.getValues?.() ?? {} as Partial<AddressData>;
    const rawArea = houseValues.area;
    const merged: HousePropertyInfoData = {
      ...houseValues,
      ...addressValues,
      area: typeof rawArea === 'string' ? (rawArea ? parseFloat(parseFloat(rawArea).toFixed(2)) : undefined) : rawArea,
      zipCode: addressValues.zipCode != null
        ? (typeof addressValues.zipCode === 'string' ? parseInt(addressValues.zipCode, 10) : addressValues.zipCode)
        : undefined,
    };
    handleFormSubmit(merged);
  };

  if (loading) return (
    <ContainerComponent>
      <Flex align="center" justify="center" style={{ height: '40vh' }}>
        <LoaderSquare/>
      </Flex>
    </ContainerComponent>
  );

  return (
    <ContainerComponent>
      <Helmet>
        <title>Podaci o kući | Osiguranje imovine - kompare.hr</title>
      </Helmet>
      <StepHeader/>
      <HousePropertyInfoForm
        ref={houseFormRef}
        data={propertyData}
        onSubmit={handleFormSubmit}
      />
      <AddressForm ref={addressFormRef} data={addressData} />
      <NavigationButtons
        handleBack={handleBack}
        handleNext={handleSubmitBoth}
        gap={64}
      />
      <FormDisclaimer
        intro={true}
      />
    </ContainerComponent>
  );
};

export default PropertyInfoHousePage;
